import { z } from "zod";

/**
 * Eval Dataset schema.
 */
export const evalDatasetSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  itemCount: z.number().int().min(0),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type EvalDataset = z.infer<typeof evalDatasetSchema>;

export const createEvalDatasetSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
});
export type CreateEvalDatasetInput = z.infer<typeof createEvalDatasetSchema>;

export const updateEvalDatasetSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(1000).optional().nullable(),
});
export type UpdateEvalDatasetInput = z.infer<typeof updateEvalDatasetSchema>;

/**
 * Eval Dataset Item schema.
 */
export const evalDatasetItemSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  datasetId: z.uuid(),
  input: z.unknown(),
  expectedOutput: z.unknown().nullable(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  createdAt: z.iso.datetime(),
});
export type EvalDatasetItem = z.infer<typeof evalDatasetItemSchema>;

export const createEvalDatasetItemSchema = z.object({
  datasetId: z.uuid(),
  input: z.unknown(),
  expectedOutput: z.unknown().optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});
export type CreateEvalDatasetItemInput = z.infer<typeof createEvalDatasetItemSchema>;

/**
 * Eval Grader type.
 */
export const evalGraderTypes = ["deterministic", "rubric", "human"] as const;
export const evalGraderTypeSchema = z.enum(evalGraderTypes);
export type EvalGraderType = z.infer<typeof evalGraderTypeSchema>;

/**
 * Eval Grader schema.
 */
export const evalGraderSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  type: evalGraderTypeSchema,
  config: z.record(z.string(), z.unknown()).default({}),
  version: z.number().int().min(1),
  promptVersion: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type EvalGrader = z.infer<typeof evalGraderSchema>;

export const createEvalGraderSchema = z.object({
  name: z.string().min(1).max(120),
  type: evalGraderTypeSchema,
  config: z.record(z.string(), z.unknown()).default({}),
  version: z.number().int().min(1).default(1),
  promptVersion: z.string().optional().nullable(),
});
export type CreateEvalGraderInput = z.infer<typeof createEvalGraderSchema>;

export const updateEvalGraderSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  type: evalGraderTypeSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  version: z.number().int().min(1).optional(),
  promptVersion: z.string().optional().nullable(),
});
export type UpdateEvalGraderInput = z.infer<typeof updateEvalGraderSchema>;

/**
 * Eval Criterion schema.
 */
export const evalCriterionSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  graderId: z.uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  threshold: z.record(z.string(), z.unknown()).default({}),
  weight: z.number().min(0),
  createdAt: z.iso.datetime(),
});
export type EvalCriterion = z.infer<typeof evalCriterionSchema>;

export const createEvalCriterionSchema = z.object({
  graderId: z.uuid(),
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional().nullable(),
  threshold: z.record(z.string(), z.unknown()).default({}),
  weight: z.number().min(0).default(1),
});
export type CreateEvalCriterionInput = z.infer<typeof createEvalCriterionSchema>;

/**
 * Eval Run status.
 */
export const evalRunStatuses = ["Running", "Completed", "Failed", "Cancelled"] as const;
export const evalRunStatusSchema = z.enum(evalRunStatuses);
export type EvalRunStatus = z.infer<typeof evalRunStatusSchema>;

/**
 * Eval Run schema.
 */
export const evalRunSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  datasetId: z.uuid(),
  graderId: z.uuid(),
  name: z.string().min(1),
  environment: z.string().min(1),
  trigger: z.string().min(1),
  status: evalRunStatusSchema,
  totalItems: z.number().int().min(0),
  completedItems: z.number().int().min(0),
  startedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type EvalRun = z.infer<typeof evalRunSchema>;

export const createEvalRunSchema = z.object({
  datasetId: z.uuid(),
  graderId: z.uuid(),
  name: z.string().min(1).max(120),
  environment: z.string().min(1).max(80),
  trigger: z.string().min(1).max(200),
});
export type CreateEvalRunInput = z.infer<typeof createEvalRunSchema>;

export const completeEvalRunSchema = z.object({
  status: evalRunStatusSchema,
});
export type CompleteEvalRunInput = z.infer<typeof completeEvalRunSchema>;

/**
 * Eval Result schema.
 */
export const evalResultSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  runId: z.uuid(),
  datasetItemId: z.uuid(),
  criterionId: z.uuid().nullable(),
  score: z.number().nullable(),
  passed: z.boolean().nullable(),
  details: z.record(z.string(), z.unknown()).default({}),
  gradedAt: z.iso.datetime(),
});
export type EvalResult = z.infer<typeof evalResultSchema>;

export const recordEvalResultSchema = z.object({
  datasetItemId: z.uuid(),
  criterionId: z.uuid().optional().nullable(),
  score: z.number().optional().nullable(),
  passed: z.boolean().optional().nullable(),
  details: z.record(z.string(), z.unknown()).default({}),
});
export type RecordEvalResultInput = z.infer<typeof recordEvalResultSchema>;

/**
 * Eval Run Summary schema.
 */
export const evalRunSummarySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  runId: z.uuid(),
  criterionId: z.uuid().nullable(),
  meanScore: z.number().nullable(),
  passRate: z.number().nullable(),
  itemCount: z.number().int().min(0),
  createdAt: z.iso.datetime(),
});
export type EvalRunSummary = z.infer<typeof evalRunSummarySchema>;

/**
 * Workflow scenario fixtures (EV3). A fixture names the gates, cost caps, and
 * reject routes a workflow must have. The console lists them; evaluation runs
 * in the agent runtime against the workflow given at the time.
 */
export const scenarioRejectRouteSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
});
export type ScenarioRejectRoute = z.infer<typeof scenarioRejectRouteSchema>;

export const scenarioExpectationsSchema = z.object({
  gates: z.array(z.string().min(1)),
  caps: z.array(z.string().min(1)),
  rejectRoutes: z.array(scenarioRejectRouteSchema),
});
export type ScenarioExpectations = z.infer<typeof scenarioExpectationsSchema>;

export const scenarioFixtureSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  expectations: scenarioExpectationsSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ScenarioFixture = z.infer<typeof scenarioFixtureSchema>;

export const scenarioFixtureListSchema = z.object({
  fixtures: z.array(scenarioFixtureSchema),
});
export type ScenarioFixtureList = z.infer<typeof scenarioFixtureListSchema>;

/**
 * Skill sample tasks (EV4). A sample task is one prompt a skill is judged on,
 * with a rubric of weighted criteria. The console lists them; the agent runtime
 * stores them and records the score a skill gets before it is enabled.
 */
export const skillSampleTaskRubricCriterionSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  weight: z.number().gt(0),
});
export type SkillSampleTaskRubricCriterion = z.infer<typeof skillSampleTaskRubricCriterionSchema>;

export const skillSampleTaskSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  skillName: z.string().min(1),
  name: z.string().min(1),
  prompt: z.string().min(1),
  rubric: z.array(skillSampleTaskRubricCriterionSchema).min(1),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type SkillSampleTask = z.infer<typeof skillSampleTaskSchema>;

export const skillSampleTaskListSchema = z.object({
  tasks: z.array(skillSampleTaskSchema),
});
export type SkillSampleTaskList = z.infer<typeof skillSampleTaskListSchema>;
